import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../lib/api.js'
import { CheckSquare, X, Edit, ChevronDown, ChevronRight, Link as LinkIcon, FileText, Eye, ShieldCheck, AlertCircle, Info, ExternalLink, Briefcase, Server, AlertTriangle } from 'lucide-react'
import Papa from 'papaparse'

const IMPLEMENTATION_STATUSES = ['Not Started', 'In Progress', 'Implemented', 'Not Applicable']

function ControlViewModal({ control, dbControl, documents = [], securityControls = [], processes = [], assets = [], risks = [], onClose }) {
  const parseJSON = (str) => {
    try { return JSON.parse(str || '[]') } catch { return [] }
  }

  const linkedDocIds = parseJSON(dbControl?.evidence_docs)
  const linkedSecCtrlIds = parseJSON(dbControl?.evidence_controls)
  const linkedProcessIds = parseJSON(dbControl?.evidence_processes)
  const linkedAssetIds = parseJSON(dbControl?.evidence_assets)
  const linkedRiskIds = parseJSON(dbControl?.evidence_risks)

  const linkedDocs = documents.filter(d => linkedDocIds.includes(String(d.id)))
  const linkedSecCtrls = securityControls.filter(sc => linkedSecCtrlIds.includes(String(sc.id)))
  const linkedProcesses = processes.filter(p => linkedProcessIds.includes(String(p.id)))
  const linkedAssets = assets.filter(a => linkedAssetIds.includes(String(a.id)))
  const linkedRisks = risks.filter(r => linkedRiskIds.includes(String(r.id)))

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 800 }}>
        <div className="modal-header">
          <div>
            <span className="font-mono text-xs" style={{ color: 'var(--accent)', fontWeight: 600 }}>{control.id}</span>
            <h2 style={{ fontSize: '1.25rem', marginTop: 2 }}>{control.name}</h2>
            <span className="badge badge-neutral" style={{ marginTop: 4 }}>{dbControl?.category}</span>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '80vh', overflow: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
            <div className="card shadow-sm" style={{ padding: 16, border: '1px solid var(--border)' }}>
              <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Applicability</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {dbControl?.applicable === 0 ? <X size={16} color="var(--danger)" /> : <ShieldCheck size={16} color="var(--success)" />}
                <span style={{ fontWeight: 500 }}>{dbControl?.applicable === 0 ? 'Not Applicable' : 'Applicable'}</span>
              </div>
            </div>
            <div className="card shadow-sm" style={{ padding: 16, border: '1px solid var(--border)' }}>
              <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Status</span>
              <span className={`badge ${statusColor(dbControl?.implementation_status || 'Not Started')}`}>
                {dbControl?.implementation_status || 'Not Started'}
              </span>
            </div>
          </div>

          <section style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Info size={16} color="var(--accent)" /> Control Description
            </h4>
            <p style={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--text-secondary)', background: 'var(--bg-base)', padding: 16, borderRadius: 8 }}>
              {dbControl?.description || 'No description available.'}
            </p>
          </section>

          <section style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>Justification</h4>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', minHeight: 40 }}>
              {dbControl?.justification || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No justification provided.</span>}
            </div>
          </section>

          <section style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>Implementation Notes</h4>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', padding: 12, borderRadius: 8, borderLeft: '3px solid var(--border)' }}>
              {dbControl?.notes || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No notes recorded.</span>}
            </div>
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <section>
              <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FileText size={14} color="var(--text-muted)" /> Linked Evidence
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {linkedDocs.map(d => (
                  <Link 
                    key={d.id} 
                    to={`/documents?search=${encodeURIComponent(d.title)}`}
                    className="flex items-center justify-between gap-8" 
                    style={{ 
                      fontSize: '0.75rem', 
                      color: 'var(--text-secondary)', 
                      padding: '6px 10px', 
                      background: 'var(--surface)', 
                      borderRadius: 6,
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      border: '1px solid transparent'
                    }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)' }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FileText size={12} /> {d.title}
                    </span>
                    <ExternalLink size={10} style={{ opacity: 0.6 }} />
                  </Link>
                ))}
                {linkedDocs.length === 0 && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No documents linked.</span>}
              </div>
            </section>
            <section>
              <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <LinkIcon size={14} color="var(--text-muted)" /> Security Controls
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {linkedSecCtrls.map(sc => (
                  <Link 
                    key={sc.id} 
                    to={`/controls-library?search=${encodeURIComponent(sc.name)}`}
                    className="flex items-center justify-between gap-8" 
                    style={{ 
                      fontSize: '0.75rem', 
                      color: 'var(--text-secondary)', 
                      padding: '6px 10px', 
                      background: 'var(--accent-dim)', 
                      borderRadius: 6,
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      border: '1px solid transparent'
                    }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)' }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ShieldCheck size={12} color="var(--accent)" /> {sc.name}
                    </span>
                    <ExternalLink size={10} style={{ opacity: 0.6 }} />
                  </Link>
                ))}
                {linkedSecCtrls.length === 0 && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No security controls linked.</span>}
              </div>
            </section>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
            <section>
              <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Briefcase size={14} color="var(--text-muted)" /> Linked Processes
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {linkedProcesses.map(p => (
                  <Link 
                    key={p.id} 
                    to={`/processes?search=${encodeURIComponent(p.process_name)}`}
                    className="flex items-center justify-between gap-8" 
                    style={{ 
                      fontSize: '0.75rem', 
                      color: 'var(--text-secondary)', 
                      padding: '6px 10px', 
                      background: 'var(--surface)', 
                      borderRadius: 6,
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      border: '1px solid transparent'
                    }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)' }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Briefcase size={12} /> {p.process_name}
                    </span>
                    <ExternalLink size={10} style={{ opacity: 0.6 }} />
                  </Link>
                ))}
                {linkedProcesses.length === 0 && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No processes linked.</span>}
              </div>
            </section>
            <section>
              <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Server size={14} color="var(--text-muted)" /> Linked Assets
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {linkedAssets.map(a => (
                  <Link 
                    key={a.id} 
                    to={`/assets?search=${encodeURIComponent(a.asset_name)}`}
                    className="flex items-center justify-between gap-8" 
                    style={{ 
                      fontSize: '0.75rem', 
                      color: 'var(--text-secondary)', 
                      padding: '6px 10px', 
                      background: 'var(--surface)', 
                      borderRadius: 6,
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      border: '1px solid transparent'
                    }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)' }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Server size={12} color="var(--accent)" /> {a.asset_name}
                    </span>
                    <ExternalLink size={10} style={{ opacity: 0.6 }} />
                  </Link>
                ))}
                {linkedAssets.length === 0 && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No assets linked.</span>}
              </div>
            </section>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', marginTop: 20 }}>
            <section>
              <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={14} color="var(--text-muted)" /> Linked Risks
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {linkedRisks.map(r => (
                  <Link 
                    key={r.id} 
                    to={`/risks`}
                    className="flex items-center justify-between gap-8" 
                    style={{ 
                      fontSize: '0.75rem', 
                      color: 'var(--text-secondary)', 
                      padding: '6px 10px', 
                      background: 'var(--surface)', 
                      borderRadius: 6,
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      border: '1px solid transparent'
                    }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--danger)'; e.currentTarget.style.color = 'var(--danger)' }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <AlertTriangle size={12} color="var(--danger)" /> {r.description}
                    </span>
                    <ExternalLink size={10} style={{ opacity: 0.6 }} />
                  </Link>
                ))}
                {linkedRisks.length === 0 && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No risks linked.</span>}
              </div>
            </section>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

// ISO 27001:2022 Annex A Controls (grouped by domain)
export const ANNEX_A = [
  {
    domain: '5. Organizational Controls',
    controls: [
      { id: '5.1', name: 'Policies for information security' },
      { id: '5.2', name: 'Information security roles and responsibilities' },
      { id: '5.3', name: 'Segregation of duties' },
      { id: '5.4', name: 'Management responsibilities' },
      { id: '5.5', name: 'Contact with authorities' },
      { id: '5.6', name: 'Contact with special interest groups' },
      { id: '5.7', name: 'Threat intelligence' },
      { id: '5.8', name: 'Information security in project management' },
      { id: '5.9', name: 'Inventory of information and other associated assets' },
      { id: '5.10', name: 'Acceptable use of information and other associated assets' },
      { id: '5.11', name: 'Return of assets' },
      { id: '5.12', name: 'Classification of information' },
      { id: '5.13', name: 'Labelling of information' },
      { id: '5.14', name: 'Information transfer' },
      { id: '5.15', name: 'Access control' },
      { id: '5.16', name: 'Identity management' },
      { id: '5.17', name: 'Authentication information' },
      { id: '5.18', name: 'Access rights' },
      { id: '5.19', name: 'Information security in supplier relationships' },
      { id: '5.20', name: 'Addressing information security within supplier agreements' },
      { id: '5.21', name: 'Managing information security in the ICT supply chain' },
      { id: '5.22', name: 'Monitoring, review and change management of supplier services' },
      { id: '5.23', name: 'Information security for use of cloud services' },
      { id: '5.24', name: 'Information security incident management planning and preparation' },
      { id: '5.25', name: 'Assessment and decision on information security events' },
      { id: '5.26', name: 'Response to information security incidents' },
      { id: '5.27', name: 'Learning from information security incidents' },
      { id: '5.28', name: 'Collection of evidence' },
      { id: '5.29', name: 'Information security during disruption' },
      { id: '5.30', name: 'ICT readiness for business continuity' },
      { id: '5.31', name: 'Legal, statutory, regulatory and contractual requirements' },
      { id: '5.32', name: 'Intellectual property rights' },
      { id: '5.33', name: 'Protection of records' },
      { id: '5.34', name: 'Privacy and protection of PII' },
      { id: '5.35', name: 'Independent review of information security' },
      { id: '5.36', name: 'Compliance with policies, rules and standards for information security' },
      { id: '5.37', name: 'Documented operating procedures' },
    ]
  },
  {
    domain: '6. People Controls',
    controls: [
      { id: '6.1', name: 'Screening' },
      { id: '6.2', name: 'Terms and conditions of employment' },
      { id: '6.3', name: 'Information security awareness, education and training' },
      { id: '6.4', name: 'Disciplinary process' },
      { id: '6.5', name: 'Responsibilities after termination or change of employment' },
      { id: '6.6', name: 'Confidentiality or non-disclosure agreements' },
      { id: '6.7', name: 'Remote working' },
      { id: '6.8', name: 'Information security event reporting' },
    ]
  },
  {
    domain: '7. Physical Controls',
    controls: [
      { id: '7.1', name: 'Physical security perimeters' },
      { id: '7.2', name: 'Physical entry' },
      { id: '7.3', name: 'Securing offices, rooms and facilities' },
      { id: '7.4', name: 'Physical security monitoring' },
      { id: '7.5', name: 'Protecting against physical and environmental threats' },
      { id: '7.6', name: 'Working in secure areas' },
      { id: '7.7', name: 'Clear desk and clear screen' },
      { id: '7.8', name: 'Equipment siting and protection' },
      { id: '7.9', name: 'Security of assets off-premises' },
      { id: '7.10', name: 'Storage media' },
      { id: '7.11', name: 'Supporting utilities' },
      { id: '7.12', name: 'Cabling security' },
      { id: '7.13', name: 'Equipment maintenance' },
      { id: '7.14', name: 'Secure disposal or re-use of equipment' },
    ]
  },
  {
    domain: '8. Technological Controls',
    controls: [
      { id: '8.1', name: 'User endpoint devices' },
      { id: '8.2', name: 'Privileged access rights' },
      { id: '8.3', name: 'Information access restriction' },
      { id: '8.4', name: 'Access to source code' },
      { id: '8.5', name: 'Secure system configuration' },
      { id: '8.6', name: 'Capacity management' },
      { id: '8.7', name: 'Protection against malware' },
      { id: '8.8', name: 'Management of technical vulnerabilities' },
      { id: '8.9', name: 'Configuration management' },
      { id: '8.10', name: 'Information deletion' },
      { id: '8.11', name: 'Data masking' },
      { id: '8.12', name: 'Data leakage prevention' },
      { id: '8.13', name: 'Information backup' },
      { id: '8.14', name: 'Redundancy of information processing facilities' },
      { id: '8.15', name: 'Logging' },
      { id: '8.16', name: 'Monitoring activities' },
      { id: '8.17', name: 'Clock synchronization' },
      { id: '8.18', name: 'Use of privileged utility programs' },
      { id: '8.19', name: 'Installation of software on operational systems' },
      { id: '8.20', name: 'Networks security' },
      { id: '8.21', name: 'Security of network services' },
      { id: '8.22', name: 'Segregation of networks' },
      { id: '8.23', name: 'Web filtering' },
      { id: '8.24', name: 'Use of cryptography' },
      { id: '8.25', name: 'Secure development life cycle' },
      { id: '8.26', name: 'Application security requirements' },
      { id: '8.27', name: 'Secure system architecture and engineering principles' },
      { id: '8.28', name: 'Secure coding' },
      { id: '8.29', name: 'Security testing in development and acceptance' },
      { id: '8.30', name: 'Outsourced development' },
      { id: '8.31', name: 'Separation of development, test and production environments' },
      { id: '8.32', name: 'Change management' },
      { id: '8.33', name: 'Test information' },
      { id: '8.34', name: 'Protection of information systems during audit testing' },
    ]
  }
]

function statusColor(s) {
  return { 'Implemented': 'badge-success', 'In Progress': 'badge-warning', 'Not Started': 'badge-danger', 'Not Applicable': 'badge-neutral' }[s] || 'badge-neutral'
}

function ControlEditModal({ control, dbControl, documents = [], securityControls = [], processes = [], assets = [], risks = [], onClose, onSave, isSaving }) {
  const parseJSON = (str) => {
    try { return JSON.parse(str || '[]') } catch { return [] }
  }

  const [form, setForm] = useState({
    control_name: control.name,
    applicable: dbControl?.applicable ?? 1,
    implementation_status: dbControl?.implementation_status || 'Not Started',
    justification: dbControl?.justification || '',
    notes: dbControl?.notes || '',
    evidence_docs: parseJSON(dbControl?.evidence_docs),
    evidence_controls: parseJSON(dbControl?.evidence_controls),
    evidence_processes: parseJSON(dbControl?.evidence_processes),
    evidence_assets: parseJSON(dbControl?.evidence_assets),
    evidence_risks: parseJSON(dbControl?.evidence_risks)
  })

  const toggleItem = (field, itemId) => {
    setForm(f => {
      const current = f[field] || []
      const next = current.includes(String(itemId))
        ? current.filter(id => id !== String(itemId))
        : [...current, String(itemId)]
      return { ...f, [field]: next }
    })
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 650 }}>
        <div className="modal-header">
          <div>
            <span className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>{control.id}</span>
            <h2 style={{ fontSize: '1.25rem', marginTop: 2 }}>{control.name}</h2>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Applicability</label>
              <select className="form-select" value={form.applicable} onChange={e => setForm(f => ({ ...f, applicable: Number(e.target.value) }))}>
                <option value={1}>Yes — Applicable</option>
                <option value={0}>No — Not Applicable</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Impl. Status</label>
              <select className="form-select" disabled={form.applicable === 0} value={form.implementation_status} onChange={e => setForm(f => ({ ...f, implementation_status: e.target.value }))}>
                {IMPLEMENTATION_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Applicability Justification</label>
            <textarea className="form-textarea" style={{ minHeight: 60 }} value={form.justification} onChange={e => setForm(f => ({ ...f, justification: e.target.value }))} placeholder="Explain why this control is included or excluded..." />
          </div>
          
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Control Evidence (Documents)</label>
              <select className="form-select" value="" onChange={e => toggleItem('evidence_docs', e.target.value)}>
                <option value="" disabled>Link a document...</option>
                {documents.map(d => (
                  <option key={d.id} value={d.id}>{form.evidence_docs.includes(String(d.id)) ? '✓ ' : ''} {d.title}</option>
                ))}
              </select>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                {form.evidence_docs.map(id => {
                  const d = documents.find(doc => String(doc.id) === String(id))
                  return d ? (
                    <span key={id} className="badge badge-accent" style={{ fontSize: '0.6875rem', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {d.title}
                      <X size={12} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => toggleItem('evidence_docs', id)} />
                    </span>
                  ) : null
                })}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Security Controls</label>
              <select className="form-select" value="" onChange={e => toggleItem('evidence_controls', e.target.value)}>
                <option value="" disabled>Link a security control...</option>
                {securityControls.map(sc => (
                  <option key={sc.id} value={sc.id}>{form.evidence_controls.includes(String(sc.id)) ? '✓ ' : ''} {sc.name}</option>
                ))}
              </select>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                {form.evidence_controls.map(id => {
                  const sc = securityControls.find(c => String(c.id) === String(id))
                  return sc ? (
                    <span key={id} className="badge badge-info" style={{ fontSize: '0.6875rem', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {sc.name}
                      <X size={12} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => toggleItem('evidence_controls', id)} />
                    </span>
                  ) : null
                })}
              </div>
            </div>
          </div>

          <div className="grid-2" style={{ marginTop: 16 }}>
            <div className="form-group">
              <label className="form-label">Processes</label>
              <select className="form-select" value="" onChange={e => toggleItem('evidence_processes', e.target.value)}>
                <option value="" disabled>Link a process...</option>
                {processes.map(p => (
                  <option key={p.id} value={p.id}>{form.evidence_processes.includes(String(p.id)) ? '✓ ' : ''} {p.process_name}</option>
                ))}
              </select>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                {form.evidence_processes.map(id => {
                  const p = processes.find(proc => String(proc.id) === String(id))
                  return p ? (
                    <span key={id} className="badge badge-neutral" style={{ fontSize: '0.6875rem', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                      {p.process_name}
                      <X size={12} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => toggleItem('evidence_processes', id)} />
                    </span>
                  ) : null
                })}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Assets</label>
              <select className="form-select" value="" onChange={e => toggleItem('evidence_assets', e.target.value)}>
                <option value="" disabled>Link an asset...</option>
                {assets.map(a => (
                  <option key={a.id} value={a.id}>{form.evidence_assets.includes(String(a.id)) ? '✓ ' : ''} {a.asset_name}</option>
                ))}
              </select>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                {form.evidence_assets.map(id => {
                  const a = assets.find(ast => String(ast.id) === String(id))
                  return a ? (
                    <span key={id} className="badge badge-neutral" style={{ fontSize: '0.6875rem', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                      {a.asset_name}
                      <X size={12} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => toggleItem('evidence_assets', id)} />
                    </span>
                  ) : null
                })}
              </div>
            </div>
          </div>

          <div className="grid-1" style={{ marginTop: 16 }}>
            <div className="form-group">
              <label className="form-label">Risks</label>
              <select className="form-select" value="" onChange={e => toggleItem('evidence_risks', e.target.value)}>
                <option value="" disabled>Link a risk...</option>
                {risks.map(r => (
                  <option key={r.id} value={r.id}>{form.evidence_risks.includes(String(r.id)) ? '✓ ' : ''} {r.description}</option>
                ))}
              </select>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                {form.evidence_risks.map(id => {
                  const r = risks.find(risk => String(risk.id) === String(id))
                  return r ? (
                    <span key={id} className="badge badge-neutral" style={{ fontSize: '0.6875rem', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                      {r.description}
                      <X size={12} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => toggleItem('evidence_risks', id)} />
                    </span>
                  ) : null
                })}
              </div>
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">Implementation Notes</label>
            <textarea className="form-textarea" style={{ minHeight: 80 }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Detail how the control is implemented in your organization..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={isSaving} onClick={() => onSave(form)}>
            {isSaving ? 'Saving…' : 'Save Compliance Data'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function Controls({ toast }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(null)
  const [viewing, setViewing] = useState(null)
  const [collapsed, setCollapsed] = useState({})
  const [filterStatus, setFilterStatus] = useState('All')

  const { data: annexControls = [] } = useQuery({ queryKey: ['annex-controls'], queryFn: api.getAnnexControls })
  const { data: securityControls = [] } = useQuery({ queryKey: ['security-controls'], queryFn: api.getSecurityControls })
  const { data: documents = [] } = useQuery({ queryKey: ['documents'], queryFn: api.getDocuments })
  const { data: processes = [] } = useQuery({ queryKey: ['processes'], queryFn: api.getProcesses })
  const { data: assets = [] } = useQuery({ queryKey: ['assets'], queryFn: api.getAssets })
  const { data: risks = [] } = useQuery({ queryKey: ['risks'], queryFn: api.getRisks })

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => api.updateAnnexControl(id, data),
    onSuccess: () => { qc.invalidateQueries(['annex-controls']); setEditing(null); toast('Annex control updated', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  // Get the pure Annex A record from DB
  const getDb = (controlId) => annexControls.find(c => c.control_id === controlId)
  
  // Get all Security controls mapped to this Annex A ID
  const getMappedSecurityControls = (controlId) => {
    const db = getDb(controlId)
    let linkedIds = []
    try {
      linkedIds = JSON.parse(db?.evidence_controls || '[]')
    } catch (e) { linkedIds = [] }
    
    // Combine both: ones with annex_control_id set AND ones explicitly linked in the JSON field
    return securityControls.filter(c => {
      const mappedIds = c.annex_control_id ? c.annex_control_id.split(',').map(id => id.trim()) : []
      return mappedIds.includes(controlId) || linkedIds.includes(String(c.id))
    })
  }

  const getStatus = (controlId) => {
    const db = getDb(controlId)
    if (!db) return 'Not Started'
    if (db.applicable === 0) return 'Not Applicable'
    return db.implementation_status || 'Not Started'
  }

  const totalApplicable = ANNEX_A.flatMap(d => d.controls).filter(c => {
    const db = getDb(c.id)
    return !db || db.applicable !== 0
  }).length

  const implemented = ANNEX_A.flatMap(d => d.controls).filter(c => getStatus(c.id) === 'Implemented').length

  const toggleDomain = (domain) => setCollapsed(prev => ({ ...prev, [domain]: !prev[domain] }))

  const exportToCSV = () => {
    const data = ANNEX_A.flatMap(domain => domain.controls.map(control => {
      const db = getDb(control.id)
      const status = getStatus(control.id)
      const mapping = getMappedSecurityControls(control.id).map(sc => sc.name).join('; ')
      return {
        'Domain': domain.domain,
        'Control ID': control.id,
        'Control Name': control.name,
        'Applicable': db?.applicable === 0 ? 'No' : 'Yes',
        'Status': status,
        'Justification': db?.justification || '',
        'Implementation Notes': db?.notes || '',
        'Security Controls Mapping': mapping
      }
    }))
    const csv = Papa.unparse(data)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', `ISO27001_Annex_A_Controls_${new Date().toISOString().split('T')[0]}.csv`)
    link.click()
  }

  const filteredDomains = ANNEX_A.map(domain => ({
    ...domain,
    controls: filterStatus === 'All' ? domain.controls : domain.controls.filter(c => getStatus(c.id) === filterStatus)
  })).filter(d => d.controls.length > 0)

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Annex A Controls</h1>
          <p>ISO 27001:2022 — 93 controls across 4 domains</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={exportToCSV}>
            <FileText size={15} /> Export CSV
          </button>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Implementation Progress</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent)' }}>{implemented}/{totalApplicable}</div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div className="progress-bar" style={{ height: 8, marginBottom: 8 }}>
          <div className="progress-fill" style={{ width: `${totalApplicable ? (implemented / totalApplicable) * 100 : 0}%`, background: 'var(--accent)' }} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {['All', 'Not Started', 'In Progress', 'Implemented', 'Not Applicable'].map(s => (
            <button key={s} className={`btn btn-sm ${filterStatus === s ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilterStatus(s)}>{s}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filteredDomains.map(domain => {
          const isCollapsed = collapsed[domain.domain]
          const domainImplemented = domain.controls.filter(c => getStatus(c.id) === 'Implemented').length
          const domainApplicable = domain.controls.filter(c => getStatus(c.id) !== 'Not Applicable').length

          return (
            <div key={domain.domain} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div
                style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-elevated)' }}
                onClick={() => toggleDomain(domain.domain)}
              >
                {isCollapsed ? <ChevronRight size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
                <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{domain.domain}</span>
                <span className="badge badge-neutral" style={{ marginLeft: 'auto' }}>{domain.controls.length} controls</span>
                <span className="badge badge-success">{domainImplemented}/{domainApplicable} done</span>
              </div>

              {!isCollapsed && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-base)' }}>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Control ID</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Control Name</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Applicable</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Status</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Security Controls</th>
                      <th style={{ padding: '10px 16px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {domain.controls.map(control => {
                      const db = getDb(control.id)
                      const status = getStatus(control.id)
                      return (
                        <tr key={control.id} style={{ borderTop: '1px solid var(--border)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <td style={{ padding: '11px 16px' }}>
                            <span className="font-mono" style={{ fontSize: '0.8125rem', color: 'var(--accent)', fontWeight: 600 }}>{control.id}</span>
                          </td>
                          <td style={{ padding: '11px 16px', fontSize: '0.875rem', color: 'var(--text-primary)' }}>{control.name}</td>
                          <td style={{ padding: '11px 16px' }}>
                            {db?.applicable === 0
                              ? <span className="badge badge-neutral">No</span>
                              : <span className="badge badge-success">Yes</span>
                            }
                          </td>
                          <td style={{ padding: '11px 16px' }}>
                            <span className={`badge ${statusColor(status)}`}>{status}</span>
                          </td>
                          <td style={{ padding: '11px 16px', fontSize: '0.8125rem', color: 'var(--text-muted)', maxWidth: 240 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {getMappedSecurityControls(control.id).map(sc => (
                                <div key={sc.id} style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)', fontWeight: 500 }}>
                                  <LinkIcon size={10} style={{ color: 'var(--accent)' }} /> {sc.name}
                                </div>
                              ))}
                              {getMappedSecurityControls(control.id).length === 0 && <span style={{ opacity: 0.5 }}>No implementation mapping</span>}
                            </div>
                          </td>
                          <td style={{ padding: '11px 16px' }}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="btn btn-ghost btn-sm" style={{ padding: 6, color: 'var(--text-muted)' }} onClick={() => setViewing({ control, dbControl: db })}>
                                <Eye size={16} />
                              </button>
                              <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={() => setEditing({ control, dbControl: db })}>
                                <Edit size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )
        })}
      </div>

      {editing && (
        <ControlEditModal
          control={editing.control}
          dbControl={editing.dbControl}
          documents={documents}
          securityControls={securityControls}
          processes={processes}
          assets={assets}
          risks={risks}
          onClose={() => setEditing(null)}
          onSave={data => updateMut.mutate({ id: editing.control.id, data })}
          isSaving={updateMut.isPending}
        />
      )}

      {viewing && (
        <ControlViewModal
          control={viewing.control}
          dbControl={viewing.dbControl}
          documents={documents}
          securityControls={securityControls}
          processes={processes}
          assets={assets}
          risks={risks}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  )
}
